import React, { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMentionSearch } from '@/hooks/useMentionSearch';

interface MentionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactElement;
}

export function MentionAutocomplete({ value, onChange, children }: MentionAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { searchUsers, users, loading } = useMentionSearch();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (mentionQuery) {
      timeoutRef.current = setTimeout(() => {
        searchUsers(mentionQuery);
      }, 300);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [mentionQuery, searchUsers]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    onChange(newValue);
    setCursorPosition(cursorPos);

    // Check if we're typing a mention
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Check if there's no space after @ (active mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionQuery(textAfterAt);
        setIsOpen(true);
        setSelectedIndex(0);
      } else {
        setIsOpen(false);
      }
    } else {
      setIsOpen(false);
    }
  };

  const insertMention = (username: string) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const newValue = 
        textBeforeCursor.substring(0, lastAtIndex) + 
        `@${username} ` + 
        textAfterCursor;
      
      onChange(newValue);
      setIsOpen(false);
      setMentionQuery('');
      
      // Focus back on textarea
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = lastAtIndex + username.length + 2;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isOpen || users.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, users.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && users[selectedIndex] && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        insertMention(users[selectedIndex].username!);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen && users.length > 0} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {React.cloneElement(children, {
          ref: textareaRef,
          value,
          onChange: handleChange,
          onKeyDown: handleKeyDown,
        })}
      </PopoverTrigger>
      <PopoverContent 
        className="w-72 p-0 z-50" 
        align="start"
        side="top"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Don't close if clicking on submit buttons or other interactive elements
          const target = e.target as HTMLElement;
          if (target.closest('button[type="submit"]')) {
            e.preventDefault();
          }
        }}
      >
        <ScrollArea className="max-h-60">
          <div className="p-2">
            {loading ? (
              <div className="text-sm text-muted-foreground p-2">Searching...</div>
            ) : (
              users.map((user, index) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    index === selectedIndex ? 'bg-accent' : 'hover:bg-accent'
                  }`}
                  onMouseDown={(e) => {
                    // Use mousedown instead of click to ensure it fires before blur
                    e.preventDefault();
                    insertMention(user.username!);
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.image || ''} />
                    <AvatarFallback>
                      {user.name?.[0] || user.username?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {user.name || user.username}
                    </div>
                    {user.name && (
                      <div className="text-xs text-muted-foreground truncate">
                        @{user.username}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
